import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  SimpleChanges
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Filter, FilterListType } from './hd-filter.service';
import { FormItem, FormListType } from '../hd-form/hd-form.service';
import {
  getCascaderMenuClassName,
  getCascaderMenuStyle,
  getCascaderShowSearch,
} from '../utils/cascader.util';
import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { InputBoolean } from 'ng-zorro-antd';
import {
  HD_FILTER_DEFAULT_SCHEME_NAME,
  HD_FILTER_SCHEME_STORE,
  HdFilterScheme,
  HdFilterSchemeStore
} from './hd-filter-scheme';

@Component({
  selector: 'hd-filter',
  templateUrl: './hd-filter.component.html',
  styleUrls: ['./hd-filter.component.less']
})
export class HdFilterComponent implements OnInit, OnChanges, OnDestroy {

  @Input() filterList: Array<Filter>;
  /** 是否启用筛选组（默认关闭） */
  @Input() @InputBoolean() enableFilterScheme = false;
  /** 筛选器标识，开启筛选组时必传 */
  @Input() filterSchemeKey: string;

  @Output() searchEvent = new EventEmitter();
  @Output() resetEvent = new EventEmitter();

  validateFilterForm: FormGroup;
  isShowMore: boolean = true;
  filterListModular: number = 0;
  private searchSubject = new Subject<{ fn: any, event: any, selectOption?: any; }>();
  private debounceTimeout: number = 0;

  private searchSubscription: Subscription | undefined;
  private schemeLoadSub: Subscription | undefined;
  private schemeActionSub: Subscription | undefined;

  /** 页面配置的初始值（虚拟「默认条件」） */
  private defaultFilterValues: { [key: string]: any } = {};
  private defaultValuesCaptured = false;

  schemeList: Array<HdFilterScheme> = [];
  currentScheme: HdFilterScheme = null;
  currentSchemeName = HD_FILTER_DEFAULT_SCHEME_NAME;
  schemeLoading = false;
  schemeSaving = false;

  switchModalVisible = false;
  switchFormList: Array<FormItem> = [];
  switchValidateForm: FormGroup;

  saveAsModalVisible = false;
  saveAsFormList: Array<FormItem> = [];
  saveAsValidateForm: FormGroup;

  readonly defaultSchemeName = HD_FILTER_DEFAULT_SCHEME_NAME;
  private readonly switchDefaultKey = '__DEFAULT__';

  constructor(
    private fb: FormBuilder,
    @Optional() @Inject(HD_FILTER_SCHEME_STORE) private schemeStore: HdFilterSchemeStore
  ) {
    this.searchSubscription = this.searchSubject.pipe(
      debounceTime(this.debounceTimeout)
    ).subscribe(({ fn, event, selectOption }) => this.triggerEvent(fn, event, selectOption));
  }

  ngOnInit() {
    this.init();
    this.tryLoadSchemes(true);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['filterList']) {
      this.init();
    }
    if (changes['enableFilterScheme'] || changes['filterSchemeKey']) {
      this.tryLoadSchemes(true);
    }
  }

  ngOnDestroy() {
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
    if (this.schemeLoadSub) {
      this.schemeLoadSub.unsubscribe();
    }
    if (this.schemeActionSub) {
      this.schemeActionSub.unsubscribe();
    }
  }

  get canUseScheme(): boolean {
    return !!(this.enableFilterScheme && this.filterSchemeKey && this.schemeStore);
  }

  private init(): void {
    this.isShowMore = true;
    if (!this.checkList(this.filterList)) {
      throw 'filter组件数据格式不规范';
    }
    if (!this.filterList) {
      this.filterList = [];
    }
    this.filterListModular = this.filterList.length % 4;
    const formGroupList = {};
    this.filterList.forEach((item, index) => {
      formGroupList[item.name] = this.fb.control(item.value, []);
      item.show = index < 7;
    });
    this.validateFilterForm = this.fb.group(formGroupList);
    if (!this.defaultValuesCaptured) {
      this.defaultFilterValues = this.clonePlain(this.getFilterData());
      this.defaultValuesCaptured = true;
    }
    this.filterList.forEach((item) => {
      if (item.onChangeEvent) {
        if (item.onChangeEventDebounceTime) {
          this.validateFilterForm.get(item.name).valueChanges
            .pipe(
              debounceTime(item.onChangeEventDebounceTime)
            )
            .subscribe(value => this.triggerEvent(item.onChangeEvent, value));
        } else {
          this.validateFilterForm.get(item.name).valueChanges.subscribe(value => this.triggerEvent(item.onChangeEvent, value));
        }
      }
    });
  }

  onSearchEvent(fn: any, event: any, onSearchEventDebounceTime: any, selectOption?: any) {
    if (onSearchEventDebounceTime) {
      this.debounceTimeout = onSearchEventDebounceTime;
      if (this.searchSubscription) {
        this.searchSubscription.unsubscribe();
      }
      this.searchSubscription = this.searchSubject.pipe(
        debounceTime(this.debounceTimeout)
      ).subscribe(({ fn, event, selectOption }) => this.triggerEvent(fn, event, selectOption));
      this.searchSubject.next({ fn, event, selectOption });
    } else {
      this.triggerEvent(fn, event, selectOption);
    }
  }

  triggerEvent(fn: any, event: any, selectOption?: any) {
    if (fn && selectOption) {
      fn(event, selectOption);
    } else if (fn) {
      fn(event);
    } else {
      return;
    }
  }

  computeOffset() {
    if (this.filterList.length === 4) {
      return 18;
    }
    return this.isShowMore ? ((this.filterListModular === 0 || this.filterList.length > 7) ? 0 : (3 - this.filterListModular) * 6) : (this.filterListModular === 0 ? 18 : (3 - this.filterListModular) * 6);
  }

  private checkList(list: Array<Filter>): boolean {
    if (!list || (list && list.length === 0)) {
      return true;
    }
    const nameList = [];
    list.forEach((item) => {
      nameList.push(item.name);
    });
    if ([...new Set(nameList)].length !== list.length) {
      throw 'filter组件数据格式不规范，存在重复name值!';
    }
    return true;
  }

  public submitForm(): void {
    this.searchEvent.emit(this.validateFilterForm.getRawValue());
  }

  public resetForm(): void {
    this.resetFilterFormWithValue();
    if (this.resetEvent.observers.length > 0) {
      this.resetEvent.emit(this.validateFilterForm.getRawValue());
    } else {
      this.searchEvent.emit(this.validateFilterForm.getRawValue());
    }
  }

  public resetFilterFormWithValue() {
    const resetValue: { [key: string]: any } = {};
    this.filterList.forEach((item) => {
      resetValue[item.name] = item.value;
    });
    this.validateFilterForm.patchValue(resetValue);
  }

  public getFilterData() {
    return this.validateFilterForm.getRawValue();
  }

  public showMore(): void {
    this.isShowMore = !this.isShowMore;
    this.filterList.forEach((item, index) => {
      item.show = this.isShowMore ? index < 7 : true;
    });
  }

  public inputClean(filter: Filter): void {
    const patchObject = {};
    patchObject[filter.name] = null;
    this.validateFilterForm.patchValue(patchObject);
  }

  isAllowClear(item: Filter): boolean {
    if (!item) {
      return true;
    }
    if (item.hdAllowClear != null) {
      return !!item.hdAllowClear;
    }
    if (item.selectOption && item.selectOption.hdAllowClear != null) {
      return !!item.selectOption.hdAllowClear;
    }
    return true;
  }

  // ---------- 筛选组 ----------

  private tryLoadSchemes(applyDefault: boolean) {
    if (!this.canUseScheme) {
      this.schemeList = [];
      this.currentScheme = null;
      this.currentSchemeName = HD_FILTER_DEFAULT_SCHEME_NAME;
      return;
    }
    this.schemeLoading = true;
    if (this.schemeLoadSub) {
      this.schemeLoadSub.unsubscribe();
    }
    this.schemeLoadSub = this.schemeStore.queryByFilterKey(this.filterSchemeKey).subscribe(
      (list) => {
        this.schemeLoading = false;
        this.schemeList = list || [];
        if (applyDefault) {
          this.applySchemeByName(HD_FILTER_DEFAULT_SCHEME_NAME, true);
        } else if (this.currentScheme && this.currentScheme.uuid) {
          const found = this.schemeList.find(s => s.uuid === this.currentScheme.uuid);
          this.currentScheme = found || null;
          this.currentSchemeName = found ? found.templateName : HD_FILTER_DEFAULT_SCHEME_NAME;
        }
      },
      (err) => {
        this.schemeLoading = false;
        this.showError('加载筛选模版失败：' + (err || ''));
        if (applyDefault) {
          this.applySchemeByName(HD_FILTER_DEFAULT_SCHEME_NAME, true);
        }
      }
    );
  }

  /** 展示用列表：保证有「默认条件」 */
  getDisplaySchemeList(): Array<HdFilterScheme> {
    const result: Array<HdFilterScheme> = [];
    const fromDb = this.schemeList.find(s => s.templateName === HD_FILTER_DEFAULT_SCHEME_NAME);
    if (fromDb) {
      result.push(fromDb);
    } else {
      result.push({
        filterKey: this.filterSchemeKey,
        templateName: HD_FILTER_DEFAULT_SCHEME_NAME,
        filterValue: JSON.stringify(this.defaultFilterValues || {})
      });
    }
    (this.schemeList || []).forEach(s => {
      if (s.templateName !== HD_FILTER_DEFAULT_SCHEME_NAME) {
        result.push(s);
      }
    });
    return result;
  }

  openSwitchModal() {
    if (!this.canUseScheme) {
      return;
    }
    const current = this.currentScheme
      || {
        filterKey: this.filterSchemeKey,
        templateName: this.currentSchemeName || HD_FILTER_DEFAULT_SCHEME_NAME,
        filterValue: ''
      };
    this.buildSwitchFormList(this.getSwitchOptionKey(current));
    this.switchModalVisible = true;
  }

  closeSwitchModal() {
    this.switchModalVisible = false;
  }

  switchFormChangeEvent(form: FormGroup) {
    this.switchValidateForm = form;
  }

  getSwitchOptionKey(item: HdFilterScheme): string {
    if (!item) {
      return this.switchDefaultKey;
    }
    if (item.uuid) {
      return item.uuid;
    }
    return this.switchDefaultKey;
  }

  private buildSwitchFormList(selectedKey: string) {
    const selectList = this.getDisplaySchemeList().map(item => {
      return {
        value: this.getSwitchOptionKey(item),
        label: item.templateName
      };
    });
    this.switchFormList = [
      {
        type: FormListType.Select,
        label: '筛选组',
        name: 'schemeKey',
        require: true,
        width: 4,
        value: selectedKey,
        placeholder: '请选择筛选组',
        selectOption: {
          value: 'value',
          label: 'label',
          hdShowSearch: true,
          selectList: selectList
        }
      }
    ];
  }

  confirmSwitch(): boolean {
    if (!this.switchValidateForm || !this.switchValidateForm.valid) {
      this.showError('请选择筛选组');
      return false;
    }
    const schemeKey = this.switchValidateForm.getRawValue().schemeKey;
    if (schemeKey == null || schemeKey === '') {
      this.showError('请选择筛选组');
      return false;
    }
    const list = this.getDisplaySchemeList();
    let target: HdFilterScheme = null;
    if (schemeKey === this.switchDefaultKey) {
      target = list.find(s => s.templateName === HD_FILTER_DEFAULT_SCHEME_NAME && !s.uuid)
        || list.find(s => s.templateName === HD_FILTER_DEFAULT_SCHEME_NAME);
    } else {
      target = list.find(s => s.uuid === schemeKey);
    }
    if (!target) {
      this.showError('请选择筛选组');
      return false;
    }
    this.applyScheme(target, true);
    this.switchModalVisible = false;
    return true;
  }

  isDefaultScheme(item: HdFilterScheme): boolean {
    return !!(item && item.templateName === HD_FILTER_DEFAULT_SCHEME_NAME);
  }

  canDeleteCurrent(): boolean {
    return !!(this.currentScheme && this.currentScheme.uuid
      && this.currentScheme.templateName !== HD_FILTER_DEFAULT_SCHEME_NAME);
  }

  saveCurrentScheme() {
    if (!this.canUseScheme) {
      return;
    }
    const name = this.currentSchemeName || HD_FILTER_DEFAULT_SCHEME_NAME;
    const body: HdFilterScheme = {
      uuid: this.currentScheme && this.currentScheme.uuid ? this.currentScheme.uuid : null,
      filterKey: this.filterSchemeKey,
      templateName: name,
      filterValue: this.serializeFilterValue()
    };
    this.schemeSaving = true;
    if (this.schemeActionSub) {
      this.schemeActionSub.unsubscribe();
    }
    this.schemeActionSub = this.schemeStore.save(body).subscribe(
      (saved) => {
        this.schemeSaving = false;
        this.showSuccess('保存成功');
        this.currentScheme = saved;
        this.currentSchemeName = saved.templateName;
        this.refreshSchemeListKeepCurrent();
      },
      (err) => {
        this.schemeSaving = false;
        this.showError('保存失败：' + (err || ''));
      }
    );
  }

  openSaveAsModal() {
    this.buildSaveAsFormList();
    this.saveAsModalVisible = true;
  }

  closeSaveAsModal() {
    this.saveAsModalVisible = false;
  }

  saveAsFormChangeEvent(form: FormGroup) {
    this.saveAsValidateForm = form;
  }

  private buildSaveAsFormList() {
    this.saveAsFormList = [
      {
        type: FormListType.Input,
        label: '筛选组名称',
        name: 'templateName',
        require: true,
        width: 4,
        maxLength: 50,
        value: null,
        placeholder: '请输入筛选组名称'
      }
    ];
  }

  confirmSaveAs(): boolean {
    if (!this.saveAsValidateForm || !this.saveAsValidateForm.valid) {
      this.showError('请输入筛选组名称');
      return false;
    }
    const name = ((this.saveAsValidateForm.getRawValue().templateName) || '').trim();
    if (!name) {
      this.showError('请输入筛选组名称');
      return false;
    }
    if (name === HD_FILTER_DEFAULT_SCHEME_NAME) {
      this.showError('不能使用「默认条件」作为筛选组名称，请使用「保存为当前」覆盖默认条件');
      return false;
    }
    const exists = (this.schemeList || []).some(s => s.templateName === name);
    if (exists) {
      this.showError('筛选组名称已存在');
      return false;
    }
    const body: HdFilterScheme = {
      filterKey: this.filterSchemeKey,
      templateName: name,
      filterValue: this.serializeFilterValue()
    };
    this.schemeSaving = true;
    if (this.schemeActionSub) {
      this.schemeActionSub.unsubscribe();
    }
    this.schemeActionSub = this.schemeStore.save(body).subscribe(
      (saved) => {
        this.schemeSaving = false;
        this.saveAsModalVisible = false;
        this.showSuccess('另存为成功');
        this.currentScheme = saved;
        this.currentSchemeName = saved.templateName;
        this.refreshSchemeListKeepCurrent();
      },
      (err) => {
        this.schemeSaving = false;
        this.showError('另存为失败：' + (err || ''));
      }
    );
    return false;
  }

  deleteCurrentScheme() {
    if (!this.canDeleteCurrent()) {
      return;
    }
    const uuid = this.currentScheme.uuid;
    this.schemeSaving = true;
    if (this.schemeActionSub) {
      this.schemeActionSub.unsubscribe();
    }
    this.schemeActionSub = this.schemeStore.delete(uuid).subscribe(
      () => {
        this.schemeSaving = false;
        this.showSuccess('删除成功');
        this.schemeList = (this.schemeList || []).filter(s => s.uuid !== uuid);
        this.applySchemeByName(HD_FILTER_DEFAULT_SCHEME_NAME, true);
      },
      (err) => {
        this.schemeSaving = false;
        this.showError('删除失败：' + (err || ''));
      }
    );
  }

  private refreshSchemeListKeepCurrent() {
    if (!this.canUseScheme) {
      return;
    }
    this.schemeStore.queryByFilterKey(this.filterSchemeKey).subscribe(
      (list) => {
        this.schemeList = list || [];
        if (this.currentScheme && this.currentScheme.uuid) {
          const found = this.schemeList.find(s => s.uuid === this.currentScheme.uuid);
          if (found) {
            this.currentScheme = found;
            this.currentSchemeName = found.templateName;
          }
        }
      },
      () => { /* ignore */ }
    );
  }

  private applySchemeByName(name: string, emitSearch: boolean) {
    const list = this.getDisplaySchemeList();
    const target = list.find(s => s.templateName === name);
    if (target) {
      this.applyScheme(target, emitSearch);
    }
  }

  private applyScheme(scheme: HdFilterScheme, emitSearch: boolean) {
    this.currentScheme = scheme && scheme.uuid ? scheme : {
      filterKey: this.filterSchemeKey,
      templateName: scheme ? scheme.templateName : HD_FILTER_DEFAULT_SCHEME_NAME,
      filterValue: scheme ? scheme.filterValue : JSON.stringify(this.defaultFilterValues || {})
    };
    this.currentSchemeName = this.currentScheme.templateName || HD_FILTER_DEFAULT_SCHEME_NAME;

    let values: any = {};
    if (this.currentSchemeName === HD_FILTER_DEFAULT_SCHEME_NAME && !(scheme && scheme.uuid)) {
      values = this.clonePlain(this.defaultFilterValues || {});
    } else {
      try {
        values = JSON.parse(scheme.filterValue || '{}');
      } catch (e) {
        values = {};
      }
      if (this.currentSchemeName === HD_FILTER_DEFAULT_SCHEME_NAME) {
        // 库中默认条件覆盖初始值：以默认值为底，再叠加库值
        values = Object.assign({}, this.defaultFilterValues || {}, values || {});
      }
    }
    this.patchFilterValues(values);
    if (emitSearch) {
      this.submitForm();
    }
  }

  private serializeFilterValue(): string {
    return JSON.stringify(this.getFilterData() || {});
  }

  private patchFilterValues(values: { [key: string]: any }) {
    if (!this.validateFilterForm || !this.filterList) {
      return;
    }
    const patch: { [key: string]: any } = {};
    this.filterList.forEach(item => {
      if (values && Object.prototype.hasOwnProperty.call(values, item.name)) {
        patch[item.name] = this.reviveFilterValue(item, values[item.name]);
      }
    });
    this.validateFilterForm.patchValue(patch);
  }

  private reviveFilterValue(filter: Filter, value: any): any {
    if (value == null) {
      return value;
    }
    if (filter.type === FilterListType.DateRange) {
      if (Array.isArray(value)) {
        return value.map(v => this.parseDateLike(v));
      }
      return value;
    }
    if (filter.type === FilterListType.Date || filter.type === FilterListType.Month) {
      return this.parseDateLike(value);
    }
    return value;
  }

  private parseDateLike(value: any): any {
    if (value == null || value instanceof Date) {
      return value;
    }
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
      const d = new Date(value);
      return isNaN(d.getTime()) ? value : d;
    }
    if (typeof value === 'number') {
      const d = new Date(value);
      return isNaN(d.getTime()) ? value : d;
    }
    return value;
  }

  private clonePlain(obj: any): any {
    try {
      return JSON.parse(JSON.stringify(obj == null ? {} : obj));
    } catch (e) {
      return {};
    }
  }

  private showSuccess(msg: string) {
    if (typeof window !== 'undefined' && (window as any).hdMessageService) {
      (window as any).hdMessageService.prototype.success(msg);
    }
  }

  private showError(msg: string) {
    if (typeof window !== 'undefined' && (window as any).hdMessageService) {
      (window as any).hdMessageService.prototype.error(msg);
    } else {
      // eslint-disable-next-line no-console
      console.error(msg);
    }
  }

  getCascaderShowSearch = getCascaderShowSearch;
  getCascaderMenuClassName = getCascaderMenuClassName;
  getCascaderMenuStyle = getCascaderMenuStyle;
}
